import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { IPushRegistrationData } from '@core/interfaces/notifications.interface';
import { environment } from '@environments/environment';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { getMessaging, getToken, MessagePayload, onMessage } from 'firebase/messaging';
import { Guid } from 'guid-typescript';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private pushToken: string = '';
  messageSignal = signal<MessagePayload | null>(null);

  private _httpClientWithoutInterceptor: HttpClient;
  private httpBackend = inject(HttpBackend);
  private launchDarklyService = inject(LaunchDarklyService);

  constructor() {
    this._httpClientWithoutInterceptor = new HttpClient(this.httpBackend);
  }

  get pushPlatformUrl(): string {
    return environment.pushPlatform;
  }

  isPushSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  requestPermission() {
    if (this.isPushSupported()) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log('Notification permission granted.');
        } else {
          console.log('Notification permission not granted.');
        }
      });
    } else {
      console.warn('Push notifications are not supported in this browser.');
    }
  }

  listen() {
    const messaging = getMessaging();
    onMessage(messaging, (payload: MessagePayload) => {
      console.log('Message received foreground. ', payload);
      if (payload?.data) {
        window.localStorage.setItem('departureId', payload.data['departureId']);
      }
      this.messageSignal.set(payload);
    });
  }

  async registerFirebasePushToken(fdGwToken: string): Promise<void> {
    const messaging = getMessaging();

    getToken(messaging, { vapidKey: environment.firebase.vapidKey })
      .then((currentToken) => {
        if (currentToken) {
          this.pushToken = currentToken;
          const pushData: IPushRegistrationData = {
            pushToken: currentToken,
            pushTokenProvider: environment.pushTokenProvider,
          };

          if (!window.localStorage.getItem('tokenRegistered')) {
            this.notificationRegistration(pushData, fdGwToken).subscribe({
              error: (err) => {
                console.log(err);
              },
            });
            window.localStorage.setItem('tokenRegistered', 'true');
          }
        } else {
          console.log('No registration token available. Request permission to generate one.');
        }
      })
      .catch((err) => {
        console.log('An error occurred while retrieving token. ', err);
      });
  }

  private notificationRegistration(pushData: IPushRegistrationData, fdGwToken: string) {
    const headers = this.getHeaders(fdGwToken);
    const url = this.pushPlatformUrl + `v1/registration/${environment.appId}/${this.pushToken}`;
    return this._httpClientWithoutInterceptor.post(url, pushData, { headers });
  }

  async notificationDeregistration(fdGwToken: string): Promise<void> {
    const url = this.pushPlatformUrl + `v1/registration/${environment.appId}/${this.pushToken}`;
    const headers = this.getHeaders(fdGwToken);

    this._httpClientWithoutInterceptor.delete(url, { headers }).subscribe({
      next: () => console.log('Push token deregistered successfully.'),
      error: (err) => console.error('Error during push token deregistration:', err),
    });
  }

  getHeaders(fdGwToken: string): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set('authorization', `Bearer ${fdGwToken}`);
    headers = headers.set('x-client', 'cs_chargingservices_frontend');
    headers = headers.set('X-Correlation-ID', Guid.create().toString());
    return headers;
  }
}
